import Vue from "vue";

/**
 * If the `el` param can vertically scroll (overflow: auto or overflow:scroll),
 * scroll the element down to the bottom.
 *
 * @param el Element to scroll down to the bottom
 */
export const scrollToBottom = (el: Element): void => {
  const scrollTopTarget = el.scrollHeight - el.clientHeight;

  // https://stackoverflow.com/questions/52276194/window-scrollto-with-options-not-working-on-microsoft-edge
  // https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-behavior
  if ("scrollBehavior" in document.documentElement.style) {
    el.scrollTo({
      left: 0,
      top: scrollTopTarget,
      behavior: "smooth",
    });
  }
  // Sad: no animation, that's for you Safari!
  else {
    el.scrollTo(0, scrollTopTarget);
  }
};

/**
 * Scroll an Element down to the bottom in a Vue.js component. To ensure
 * that the UI is updated before the scroll, `$nextTick()` is called.
 *
 * @param component the Vuejs component where a scroll-down-to-bottom is
 * expected to occur
 * @param el the element to scroll down to the bottom. If undefined, the
 * component root element, `this.$el`, is used
 * @see scrollToBottom
 */
export const vueScrollToBottom = async <V extends Vue>(
  component: V,
  el?: Element
): Promise<void> => {
  // Ensure that UI is up-to-date
  await component.$nextTick();

  // Select the element to scroll
  const toScrollEl = el || component.$el;

  // Scroll
  scrollToBottom(toScrollEl);
};
